import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { 
    CalendarDays, Gift, Leaf, LogOut, Menu, X,
    Home, MessageSquare, UtensilsCrossed, BookOpen,
    Heart, ClipboardList, Bus
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';

// Import refactored section components
import { 
    ManagerMessageSection, 
    ManagerEventsSection, 
    ManagerOffersSection, 
    ManagerTipsSection,
    ManagerNoticesSection,
    API,
    TIP_CATEGORIES,
    WELLNESS_CATEGORIES,
    TIP_SEASONS,
    formatDateTime
} from '../components/manager';
import { ManagerTransportSection } from '../components/manager/ManagerTransportSection';

export function ManagerPage() {
    const navigate = useNavigate();
    const { token, logout, user } = useAuth();
    const { organization, setOrganization } = useOrganization();
    const [activeSection, setActiveSection] = useState('events');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Determine if this is a residential organization
    const isResidential = organization?.type === 'residential' || organization?.slug === 'elysian';
    const orgName = organization?.name || 'Manager';
    const themeColor = isResidential ? 'bg-stone-700' : 'bg-emerald-700';
    const themeColorHover = isResidential ? 'hover:bg-stone-600' : 'hover:bg-emerald-600';
    
    // Data states
    const [events, setEvents] = useState([]);
    const [offers, setOffers] = useState([]);
    const [tips, setTips] = useState([]);
    const [managerMessage, setManagerMessage] = useState({ message: '', updated_at: null, updated_by: null });
    const [savingMessage, setSavingMessage] = useState(false);
    
    // Modal states
    const [eventModal, setEventModal] = useState({ open: false, data: null });
    const [offerModal, setOfferModal] = useState({ open: false, data: null });
    const [tipModal, setTipModal] = useState({ open: false, data: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchAllData();
        // Fetch organization data for the manager using /current endpoint
        const fetchOrgData = async () => {
            if (token) {
                try {
                    const response = await axios.get(`${API}/organization/current`, { headers });
                    if (response.data) {
                        setOrganization(response.data);
                    }
                } catch (err) {
                    console.error('Error fetching organization:', err);
                }
            }
        };
        fetchOrgData();
    }, [user, token]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [eventsRes, offersRes, tipsRes, messageRes] = await Promise.all([
                axios.get(`${API}/manager/events`, { headers }),
                axios.get(`${API}/manager/offers`, { headers }),
                axios.get(`${API}/manager/tips`, { headers }),
                axios.get(`${API}/manager/message`, { headers }),
            ]);
            
            setEvents(eventsRes.data);
            setOffers(offersRes.data);
            setTips(tipsRes.data);
            setManagerMessage(messageRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Event CRUD
    const handleSaveEvent = async () => {
        const data = eventModal.data;
        if (!data.title || !data.description || !data.date_time) {
            toast.error('Please fill in all required fields');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/manager/events` : `${API}/manager/events/${data.id}`;
        const method = isNew ? 'post' : 'put';
        
        try {
            await axios[method](endpoint, data, { headers });
            toast.success(`Event ${isNew ? 'created' : 'updated'}`);
            setEventModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error(`Failed to save event`);
        }
    };

    const handleDeleteEvent = async (id) => {
        try {
            await axios.delete(`${API}/manager/events/${id}`, { headers });
            toast.success('Event deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete event');
        }
    };

    const openNewEventModal = () => {
        setEventModal({
            open: true,
            data: {
                title: '',
                description: '',
                category: 'general',
                date_time: '',
                end_time: '',
                location: 'Burston Garden Centre',
                contact_phone: '01727 832444',
                contact_email: 'info@burston.co.uk',
                price: 'Free',
                image_url: '',
                booking_url: '',
                is_recurring: false,
                is_active: true
            }
        });
    };

    // Offer CRUD
    const handleSaveOffer = async () => {
        const data = offerModal.data;
        if (!data.title || !data.description) {
            toast.error('Please fill in all required fields');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/manager/offers` : `${API}/manager/offers/${data.id}`;
        const method = isNew ? 'post' : 'put';
        
        try {
            await axios[method](endpoint, data, { headers });
            toast.success(`Offer ${isNew ? 'created' : 'updated'}`);
            setOfferModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error(`Failed to save offer`);
        }
    };

    const handleDeleteOffer = async (id) => {
        try {
            await axios.delete(`${API}/manager/offers/${id}`, { headers });
            toast.success('Offer deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete offer');
        }
    };

    const openNewOfferModal = () => {
        setOfferModal({
            open: true,
            data: {
                title: '',
                description: '',
                category: 'general',
                discount_type: 'percentage',
                discount_value: 0,
                valid_from: new Date().toISOString().slice(0, 10),
                valid_to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                terms_conditions: '',
                is_members_only: false,
                is_active: true
            }
        });
    };

    // Tip CRUD
    const handleSaveTip = async () => {
        const data = tipModal.data;
        if (!data.title || !data.description) {
            toast.error('Please fill in all required fields');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/manager/tips` : `${API}/manager/tips/${data.id}`;
        const method = isNew ? 'post' : 'put';
        
        try {
            await axios[method](endpoint, data, { headers });
            toast.success(`Tip ${isNew ? 'created' : 'updated'}`);
            setTipModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error(`Failed to save tip`);
        }
    };

    const handleDeleteTip = async (id) => {
        try {
            await axios.delete(`${API}/manager/tips/${id}`, { headers });
            toast.success('Tip deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete tip');
        }
    };

    const openNewTipModal = () => {
        setTipModal({
            open: true,
            data: {
                title: '',
                description: '',
                category: 'general',
                season: 'all_year',
                is_active: true
            }
        });
    };

    // Save manager message
    const handleSaveMessage = async () => {
        setSavingMessage(true);
        try {
            const response = await axios.put(`${API}/manager/message`, {
                message: managerMessage.message
            }, { headers });
            setManagerMessage(response.data);
            toast.success('Message updated successfully!');
        } catch (error) {
            toast.error('Failed to save message');
        } finally {
            setSavingMessage(false);
        }
    };

    // Navigation items for manager - different based on organization type
    const navItems = isResidential ? [
        { id: 'message', icon: MessageSquare, label: 'Homepage Message' },
        { id: 'events', icon: CalendarDays, label: 'Activities & Events' },
        { id: 'offers', icon: Gift, label: 'Member Benefits' },
        { id: 'transport', icon: Bus, label: 'Minibus Trips' },
        { id: 'tips', icon: Heart, label: 'Wellness Tips' },
        { id: 'notices', icon: ClipboardList, label: 'Community Notices' },
    ] : [
        { id: 'message', icon: MessageSquare, label: 'Homepage Message' },
        { id: 'events', icon: CalendarDays, label: 'Events' },
        { id: 'offers', icon: Gift, label: 'Offers' },
        { id: 'transport', icon: Bus, label: 'Minibus Trips' },
        { id: 'tips', icon: Leaf, label: 'Garden Tips' },
        { id: 'notices', icon: ClipboardList, label: 'Community Notices' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleString('en-GB', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-background flex flex-col md:flex-row" data-testid="manager-page">
            {/* Mobile Header */}
            <div className={`md:hidden sticky top-0 left-0 right-0 ${themeColor} z-40 p-4 flex items-center justify-between`}>
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-2" data-testid="manager-mobile-menu-btn">
                    {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <h1 className="text-white font-bold">{isResidential ? 'Residence Manager' : 'Manager Panel'}</h1>
                <button onClick={handleLogout} className="text-white p-2">
                    <LogOut className="w-5 h-5" />
                </button>
            </div>

            {/* Mobile Navigation Dropdown */}
            {sidebarOpen && (
                <div className={`md:hidden ${themeColor} border-b border-white/10 p-4 z-30`}>
                    <nav className="space-y-1">
                        {navItems.map(item => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setActiveSection(item.id);
                                    setSidebarOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                    activeSection === item.id 
                                        ? 'bg-white/20 text-white' 
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                                data-testid={`manager-nav-${item.id}`}
                            >
                                <item.icon className="w-5 h-5" />
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* Desktop Sidebar */}
            <aside className={`hidden md:flex md:flex-col md:w-64 md:min-h-screen ${themeColor} flex-shrink-0`}>
                <div className="p-4 border-b border-white/10">
                    <h1 className="text-xl font-bold text-white">{isResidential ? 'Residence Manager' : 'Manager Panel'}</h1>
                    <p className="text-white/60 text-sm">{user?.email}</p>
                    {organization && <p className="text-white/40 text-xs mt-1">{organization.name}</p>}
                </div>
                
                <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
                    <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2 px-3">
                        Public Content
                    </p>
                    {navItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                activeSection === item.id 
                                    ? 'bg-white/20 text-white' 
                                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                            }`}
                            data-testid={`manager-nav-desktop-${item.id}`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/10 space-y-2">
                    <button 
                        onClick={() => navigate('/manager/dining-services')} 
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                    >
                        <UtensilsCrossed className="w-5 h-5" />
                        <span>Dining & Services CMS</span>
                    </button>
                    <button 
                        onClick={() => navigate('/manager/welcome-guide')} 
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors"
                        data-testid="manager-welcome-guide-link"
                    >
                        <BookOpen className="w-5 h-5" />
                        <span>Welcome Guide CMS</span>
                    </button>
                    <button 
                        onClick={() => navigate('/')} 
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                    >
                        <Home className="w-5 h-5" />
                        <span>View Public Site</span>
                    </button>
                    <button 
                        onClick={handleLogout} 
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                        data-testid="manager-logout-btn"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-auto">
                <div className="max-w-5xl mx-auto">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Homepage Message Section */}
                            {activeSection === 'message' && (
                                <ManagerMessageSection
                                    managerMessage={managerMessage}
                                    setManagerMessage={setManagerMessage}
                                    savingMessage={savingMessage}
                                    onSave={handleSaveMessage}
                                    isResidential={isResidential}
                                />
                            )}

                            {/* Events Section */}
                            {activeSection === 'events' && (
                                <ManagerEventsSection
                                    events={events}
                                    onOpenNewModal={openNewEventModal}
                                    onEdit={(event) => setEventModal({ open: true, data: event })}
                                    onDelete={(event) => setDeleteConfirm({ open: true, type: 'event', id: event.id, name: event.title })}
                                    isResidential={isResidential}
                                />
                            )}

                            {/* Offers Section */}
                            {activeSection === 'offers' && (
                                <ManagerOffersSection
                                    offers={offers}
                                    onOpenNewModal={openNewOfferModal}
                                    onEdit={(offer) => setOfferModal({ open: true, data: offer })}
                                    onDelete={(offer) => setDeleteConfirm({ open: true, type: 'offer', id: offer.id, name: offer.title })}
                                    isResidential={isResidential}
                                />
                            )}

                            {/* Tips Section */}
                            {activeSection === 'tips' && (
                                <ManagerTipsSection
                                    tips={tips}
                                    onOpenNewModal={openNewTipModal}
                                    onEdit={(tip) => setTipModal({ open: true, data: tip })}
                                    onDelete={(tip) => setDeleteConfirm({ open: true, type: 'tip', id: tip.id, name: tip.title })}
                                    isResidential={isResidential}
                                />
                            )}

                            {/* Transport Section */}
                            {activeSection === 'transport' && (
                                <ManagerTransportSection 
                                    token={token} 
                                    organizationName={orgName} 
                                />
                            )}

                            {/* Notices Section */}
                            {activeSection === 'notices' && (
                                <ManagerNoticesSection isResidential={isResidential} />
                            )}
                        </>
                    )}
                </div>
            </main>

            {/* Event Modal */}
            <Dialog open={eventModal.open} onOpenChange={(open) => !open && setEventModal({ open: false, data: null })}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{eventModal.data?.id ? 'Edit Event' : 'Create Event'}</DialogTitle>
                    </DialogHeader>
                    {eventModal.data && (
                        <div className="space-y-4">
                            <div>
                                <Label>Title *</Label>
                                <Input 
                                    value={eventModal.data.title} 
                                    onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value }}))}
                                    data-testid="event-title-input"
                                />
                            </div>
                            <div>
                                <Label>Description *</Label>
                                <Textarea 
                                    value={eventModal.data.description} 
                                    onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value }}))}
                                    rows={3}
                                    data-testid="event-description-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Category</Label>
                                    <select 
                                        className="w-full border rounded-md p-2"
                                        value={eventModal.data.category}
                                        onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, category: e.target.value }}))}
                                    >
                                        <option value="general">General</option>
                                        <option value="dining">Dining</option>
                                        <option value="workshop">Workshop</option>
                                        <option value="social">Social</option>
                                        <option value="special">Special</option>
                                    </select>
                                </div>
                                <div>
                                    <Label>Price</Label>
                                    <Input 
                                        value={eventModal.data.price} 
                                        onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, price: e.target.value }}))}
                                        placeholder="Free or £10"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Date & Time *</Label>
                                    <Input 
                                        type="datetime-local" 
                                        value={eventModal.data.date_time?.slice(0, 16)} 
                                        onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, date_time: e.target.value }}))}
                                        data-testid="event-datetime-input"
                                    />
                                </div>
                                <div>
                                    <Label>End Time</Label>
                                    <Input 
                                        type="datetime-local" 
                                        value={eventModal.data.end_time?.slice(0, 16) || ''} 
                                        onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, end_time: e.target.value }}))}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Location</Label>
                                <Input 
                                    value={eventModal.data.location} 
                                    onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, location: e.target.value }}))}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Contact Phone</Label>
                                    <Input 
                                        value={eventModal.data.contact_phone} 
                                        onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, contact_phone: e.target.value }}))}
                                    />
                                </div>
                                <div>
                                    <Label>Contact Email</Label>
                                    <Input 
                                        value={eventModal.data.contact_email} 
                                        onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, contact_email: e.target.value }}))}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Image URL</Label>
                                <Input 
                                    value={eventModal.data.image_url || ''} 
                                    onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, image_url: e.target.value }}))}
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <Label>Booking URL</Label>
                                <Input 
                                    value={eventModal.data.booking_url || ''} 
                                    onChange={(e) => setEventModal(prev => ({ ...prev, data: { ...prev.data, booking_url: e.target.value }}))}
                                    placeholder="https://www.burston.co.uk/event/..."
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Switch 
                                        checked={eventModal.data.is_recurring}
                                        onCheckedChange={(checked) => setEventModal(prev => ({ ...prev, data: { ...prev.data, is_recurring: checked }}))}
                                    />
                                    <Label>Recurring Event</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch 
                                        checked={eventModal.data.is_active}
                                        onCheckedChange={(checked) => setEventModal(prev => ({ ...prev, data: { ...prev.data, is_active: checked }}))}
                                    />
                                    <Label>Active</Label>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEventModal({ open: false, data: null })}>Cancel</Button>
                        <Button onClick={handleSaveEvent} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-event-btn">
                            {eventModal.data?.id ? 'Update' : 'Create'} Event
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Offer Modal */}
            <Dialog open={offerModal.open} onOpenChange={(open) => !open && setOfferModal({ open: false, data: null })}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{offerModal.data?.id ? 'Edit Offer' : 'Create Offer'}</DialogTitle>
                    </DialogHeader>
                    {offerModal.data && (
                        <div className="space-y-4">
                            <div>
                                <Label>Title *</Label>
                                <Input 
                                    value={offerModal.data.title} 
                                    onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value }}))}
                                    data-testid="offer-title-input"
                                />
                            </div>
                            <div>
                                <Label>Description *</Label>
                                <Textarea 
                                    value={offerModal.data.description} 
                                    onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value }}))}
                                    rows={3}
                                    data-testid="offer-description-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Category</Label>
                                    <select 
                                        className="w-full border rounded-md p-2"
                                        value={offerModal.data.category}
                                        onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, category: e.target.value }}))}
                                    >
                                        <option value="general">General</option>
                                        <option value="restaurant">Restaurant</option>
                                        <option value="plants">Plants</option>
                                        <option value="delivery">Delivery</option>
                                    </select>
                                </div>
                                <div>
                                    <Label>Discount Type</Label>
                                    <select 
                                        className="w-full border rounded-md p-2"
                                        value={offerModal.data.discount_type}
                                        onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, discount_type: e.target.value }}))}
                                    >
                                        <option value="percentage">Percentage Off</option>
                                        <option value="fixed">Fixed Amount Off</option>
                                        <option value="free_item">Free Item</option>
                                        <option value="free_delivery">Free Delivery</option>
                                    </select>
                                </div>
                            </div>
                            {(offerModal.data.discount_type === 'percentage' || offerModal.data.discount_type === 'fixed') && (
                                <div>
                                    <Label>Discount Value {offerModal.data.discount_type === 'percentage' ? '(%)' : '(£)'}</Label>
                                    <Input 
                                        type="number"
                                        value={offerModal.data.discount_value} 
                                        onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, discount_value: parseInt(e.target.value) || 0 }}))}
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Valid From</Label>
                                    <Input 
                                        type="date" 
                                        value={offerModal.data.valid_from?.slice(0, 10)} 
                                        onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, valid_from: e.target.value }}))}
                                    />
                                </div>
                                <div>
                                    <Label>Valid To</Label>
                                    <Input 
                                        type="date" 
                                        value={offerModal.data.valid_to?.slice(0, 10)} 
                                        onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, valid_to: e.target.value }}))}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Terms & Conditions</Label>
                                <Textarea 
                                    value={offerModal.data.terms_conditions || ''} 
                                    onChange={(e) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, terms_conditions: e.target.value }}))}
                                    rows={2}
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Switch 
                                        checked={offerModal.data.is_members_only}
                                        onCheckedChange={(checked) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, is_members_only: checked }}))}
                                    />
                                    <Label>Members Only</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch 
                                        checked={offerModal.data.is_active}
                                        onCheckedChange={(checked) => setOfferModal(prev => ({ ...prev, data: { ...prev.data, is_active: checked }}))}
                                    />
                                    <Label>Active</Label>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOfferModal({ open: false, data: null })}>Cancel</Button>
                        <Button onClick={handleSaveOffer} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-offer-btn">
                            {offerModal.data?.id ? 'Update' : 'Create'} Offer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Tip Modal */}
            <Dialog open={tipModal.open} onOpenChange={(open) => !open && setTipModal({ open: false, data: null })}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{tipModal.data?.id ? (isResidential ? 'Edit Wellness Tip' : 'Edit Garden Tip') : (isResidential ? 'Create Wellness Tip' : 'Create Garden Tip')}</DialogTitle>
                    </DialogHeader>
                    {tipModal.data && (
                        <div className="space-y-4">
                            <div>
                                <Label>Title *</Label>
                                <Input 
                                    value={tipModal.data.title} 
                                    onChange={(e) => setTipModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value }}))}
                                    data-testid="tip-title-input"
                                />
                            </div>
                            <div>
                                <Label>Description *</Label>
                                <Textarea 
                                    value={tipModal.data.description} 
                                    onChange={(e) => setTipModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value }}))}
                                    rows={5}
                                    data-testid="tip-description-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Category</Label>
                                    <select 
                                        className="w-full border rounded-md p-2"
                                        value={tipModal.data.category}
                                        onChange={(e) => setTipModal(prev => ({ ...prev, data: { ...prev.data, category: e.target.value }}))}
                                    >
                                        {(isResidential ? WELLNESS_CATEGORIES : TIP_CATEGORIES).map(cat => (
                                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Label>Season</Label>
                                    <select 
                                        className="w-full border rounded-md p-2"
                                        value={tipModal.data.season}
                                        onChange={(e) => setTipModal(prev => ({ ...prev, data: { ...prev.data, season: e.target.value }}))}
                                    >
                                        {TIP_SEASONS.map(season => (
                                            <option key={season.value} value={season.value}>{season.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch 
                                    checked={tipModal.data.is_active}
                                    onCheckedChange={(checked) => setTipModal(prev => ({ ...prev, data: { ...prev.data, is_active: checked }}))}
                                />
                                <Label>Active</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTipModal({ open: false, data: null })}>Cancel</Button>
                        <Button onClick={handleSaveTip} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-tip-btn">
                            {tipModal.data?.id ? 'Update' : 'Create'} Tip
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: '', id: null, name: '' })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Delete</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })}>
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={() => {
                                if (deleteConfirm.type === 'event') handleDeleteEvent(deleteConfirm.id);
                                else if (deleteConfirm.type === 'offer') handleDeleteOffer(deleteConfirm.id);
                                else if (deleteConfirm.type === 'tip') handleDeleteTip(deleteConfirm.id);
                            }}
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ManagerPage;
